import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card, Button, Spinner, Form } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartArea, faChartBar, faChartLine } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL, END_POINT_ADMIN, END_POINT_COMPANY, END_POINT_QUOTES_TRENDS } from "../const";

const PERIODS = [
  { key: "7d", label: "7D", name: "Last 7 days" },
  { key: "15d", label: "15D", name: "Last 15 days" },
  { key: "1m", label: "1M", name: "Last month" },
  { key: "3m", label: "3M", name: "Last 3 months" },
  { key: "6m", label: "6M", name: "Last 6 months" },
  { key: "9m", label: "9M", name: "Last 9 months" },
  { key: "12m", label: "12M", name: "Last 12 months" },
];

const MONTHLY_PERIODS = ["3m", "6m", "9m", "12m"];
const STATUSES = ["Live", "Won", "Closed", "Lost"];
const VIEW_ALL_ROLES = ["admin", "super admin", "internal sales"];
const canViewAllQuotes = (roleName = "") =>
  VIEW_ALL_ROLES.includes(String(roleName).toLowerCase());
const number = (value, digits = 0) => Number(value).toLocaleString("en-IN", { maximumFractionDigits: digits });
const money = value => "\u20B9" + number(value, 2);
const fullDate = date => new Date(date + "T00:00:00").toLocaleDateString("en-IN", {
  day: "numeric", month: "short", year: "numeric",
});
// Monthly points cover the whole calendar month of their date (handles leap years).
const monthRange = date => {
  const [year, month] = date.split("-").map(Number);
  const last = new Date(year, month, 0).getDate();
  return fullDate(year + "-" + String(month).padStart(2, "0") + "-01") + " – " +
    fullDate(year + "-" + String(month).padStart(2, "0") + "-" + String(last).padStart(2, "0"));
};
const axisValue = (value, metric) => (metric === "amount" ? "\u20B9" : "") +
  new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const validDate = value => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value + "T00:00:00Z")) &&
  new Date(value + "T00:00:00Z").toISOString().slice(0, 10) === value;

function readTrendData(response) {
  const data = response.data?.data;
  if (!response.data?.status || !data || !Array.isArray(data.series) ||
      !Number.isSafeInteger(data.totals?.quotes) || data.totals.quotes < 0 || !Number.isFinite(data.totals?.amount) ||
      data.series.some(item => !validDate(item?.date) || typeof item.label !== "string" ||
        !Number.isSafeInteger(item.quotes) || item.quotes < 0 || !Number.isFinite(item.amount))) {
    throw new Error("Invalid quote trends response");
  }
  return {
    totals: { quotes: Number(data.totals.quotes), amount: Number(data.totals.amount) },
    series: data.series.map(item => ({
      ...item, quotes: Number(item.quotes), amount: Number(item.amount),
    })).sort((a, b) => a.date.localeCompare(b.date)),
  };
}

function chartDomain(values, metric) {
  const low = Math.min(0, ...values);
  const high = Math.max(0, ...values);
  const rawStep = (high - low || 4) / 4;
  const power = 10 ** Math.floor(Math.log10(rawStep));
  const fraction = rawStep / power;
  let step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
  step = Math.max(metric === "quotes" ? 1 : 0.01, step);
  const min = Math.floor(low / step) * step;
  const max = Math.ceil(high / step) * step || (low < 0 ? 0 : step * 4);
  const ticks = [];
  for (let value = min; value <= max + step / 100; value += step) ticks.push(value);
  return { min, max, ticks };
}

let chartSequence = 0;

export function TrendPlot({ series, metric, view, average, monthly }) {
  const container = useRef(null);
  const [width, setWidth] = useState(800);
  const [activeIndex, setActiveIndex] = useState(null);
  const [gradientId] = useState(() => "quote-trend-fill-" + (++chartSequence));
  const metricLabel = metric === "quotes" ? "Quote count" : "Quote value";

  useLayoutEffect(() => {
    const resize = () => setWidth(container.current?.getBoundingClientRect().width || 800);
    resize();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    if (observer) observer.observe(container.current);
    else window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  const height = 280;
  const left = metric === "amount" ? 64 : 40;
  const right = width - 16;
  const top = 20;
  const bottom = height - 38;
  const plotWidth = Math.max(1, right - left);
  const values = series.map(item => item[metric]);
  const domain = chartDomain(values, metric);
  const y = value => bottom - ((value - domain.min) / (domain.max - domain.min)) * (bottom - top);
  const step = plotWidth / series.length;
  const points = series.map((item, index) => ({ x: left + step * (index + 0.5), y: y(item[metric]) }));
  const line = points.map((point, index) => (index ? "L" : "M") + point.x + "," + point.y).join(" ");
  const area = line + " L" + points[points.length - 1].x + "," + y(0) + " L" + points[0].x + "," + y(0) + " Z";
  const labelCount = width < 480 ? 3 : width < 760 ? 5 : 7;
  // Spread date labels evenly and always include the beginning and end of the range.
  const labelIndexes = new Set();
  // Monthly charts label every month; daily charts thin the labels to avoid overlap.
  const visibleLabels = monthly ? series.length : Math.min(labelCount, series.length);
  const compactMonths = monthly && step < 56; // too narrow for "Nov 2025", so drop the year
  for (let i = 0; i < visibleLabels; i += 1) {
    labelIndexes.add(visibleLabels === 1 ? 0 : Math.round(i * (series.length - 1) / (visibleLabels - 1)));
  }
  const active = activeIndex === null ? null : series[activeIndex];
  const activePoint = activeIndex === null ? null : points[activeIndex];
  const selectPointer = event => {
    const bounds = container.current.getBoundingClientRect();
    setActiveIndex(Math.max(0, Math.min(series.length - 1, Math.floor((event.clientX - bounds.left - left) / step))));
  };
  const selectKey = event => {
    const current = activeIndex ?? series.length - 1;
    const next = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: series.length - 1 }[event.key];
    if (next !== undefined) {
      event.preventDefault();
      setActiveIndex(Math.max(0, Math.min(series.length - 1, next)));
    } else if (event.key === "Escape") {
      setActiveIndex(null);
    }
  };

  return (
    <div
      className="quotes-trend-plot"
      ref={container}
      tabIndex={0}
      role="group"
      aria-label={metricLabel + " by offer date"}
      aria-describedby={gradientId + "-help"}
      onPointerMove={selectPointer}
      onPointerDown={selectPointer}
      onPointerLeave={event => { if (event.pointerType !== "touch" && document.activeElement !== container.current) setActiveIndex(null); }}
      onFocus={() => setActiveIndex(index => index ?? series.length - 1)}
      onBlur={() => setActiveIndex(null)}
      onKeyDown={selectKey}
    >
      <svg viewBox={"0 0 " + width + " " + height} width="100%" height={height} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--trend-color)" stopOpacity="0.26" />
            <stop offset="100%" stopColor="var(--trend-color)" stopOpacity="0.015" />
          </linearGradient>
        </defs>
        {domain.ticks.map((tick, index) => (
          <g key={index}>
            <line x1={left} x2={right} y1={y(tick)} y2={y(tick)} className="quotes-trend-plot__grid" />
            <text x={left - 10} y={y(tick) + 4} textAnchor="end" className="quotes-trend-plot__label">{axisValue(tick, metric)}</text>
          </g>
        ))}
        {view === "area" ? (
          <>
            <path d={area} fill={"url(#" + gradientId + ")"} />
            <path d={line} className="quotes-trend-plot__line" />
            {series.length <= 15 && points.map((point, index) => (
              <circle key={index} cx={point.x} cy={point.y} r="3.5" className="quotes-trend-plot__dot" />
            ))}
          </>
        ) : points.map((point, index) => (
          <rect key={index} x={point.x - Math.max(1, series.length <= 12 ? Math.max(28, step * 0.55) : step * 0.62) / 2} y={Math.min(point.y, y(0))}
            width={Math.max(1, series.length <= 12 ? Math.max(28, step * 0.55) : step * 0.62)} height={Math.max(0, Math.abs(y(0) - point.y))}
            rx={Math.min(4, step * 0.18)} className="quotes-trend-plot__bar" opacity={activeIndex === null || activeIndex === index ? 0.9 : 0.4} />
        ))}
        <line x1={left} x2={right} y1={y(average)} y2={y(average)} className="quotes-trend-plot__average" />
        {[...labelIndexes].map(index => (
          <text key={index} x={points[index].x} y={height - 12}
            textAnchor={monthly ? "middle" : index === 0 ? "start" : index === series.length - 1 ? "end" : "middle"}
            className="quotes-trend-plot__label">{compactMonths ? series[index].label.split(" ")[0] : series[index].label}</text>
        ))}
        {activePoint && (
          <g>
            <line x1={activePoint.x} x2={activePoint.x} y1={top} y2={bottom} className="quotes-trend-plot__cursor" />
            <circle cx={activePoint.x} cy={activePoint.y} r="6" className="quotes-trend-plot__dot" />
          </g>
        )}
      </svg>
      {active && (
        <div className="quotes-trend-tooltip" role="status"
          style={{ left: Math.max(8, Math.min(width - 208, activePoint.x - 100)) }}>
          <strong>{monthly ? monthRange(active.date) : fullDate(active.date)}</strong>
          <span>Quotes <b>{number(active.quotes)}</b></span>
          <span>Quote value <b>{money(active.amount)}</b></span>
        </div>
      )}
      <span id={gradientId + "-help"} className="visually-hidden">
        Use Left and Right arrow keys to explore each day, or Home and End to jump to the first and last day.
      </span>
    </div>
  );
}

export default function QuotesTrendChart() {
  const [period, setPeriod] = useState("7d");
  const [metric, setMetric] = useState("quotes");
  const [view, setView] = useState("area");
  const [userId, setUserId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [status, setStatus] = useState("");
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [retry, setRetry] = useState(0);
  const [request, setRequest] = useState({ key: "", loading: true, data: null, error: "" });
  const token = sessionStorage.getItem("quotetoken")?.replace(/^"|"$/g, "");
  const userDetail = JSON.parse(sessionStorage.getItem("quoteadmin") || "null");
  const showUserFilter = canViewAllQuotes(userDetail?.role_name);
  const requestKey = [token, period, retry, userId, companyId, status].join(":");

  useEffect(() => {
    const headers = { Accept: "application/json", Authorization: "Bearer " + token };
    const loadOptions = async () => {
      try {
        const [companyResponse, adminResponse] = await Promise.all([
          axios.get(API_URL + END_POINT_COMPANY, { headers }),
          showUserFilter ? axios.get(API_URL + END_POINT_ADMIN, { headers }) : Promise.resolve(null),
        ]);
        if (companyResponse.data?.status && Array.isArray(companyResponse.data.data)) {
          setCompanies(companyResponse.data.data.filter((company) => !company.deleted_at));
        }
        if (adminResponse?.data?.status && Array.isArray(adminResponse.data.data)) {
          setUsers(adminResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching trend filters:", error);
      }
    };
    loadOptions();
  }, [token, showUserFilter]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setRequest({ key: requestKey, loading: true, data: null, error: "" });
    axios.get(API_URL + END_POINT_QUOTES_TRENDS, {
      params: {
        period,
        ...(showUserFilter && userId ? { se_id: userId } : {}),
        ...(companyId ? { company_id: companyId } : {}),
        ...(status ? { status } : {}),
      },
      headers: { Accept: "application/json", Authorization: "Bearer " + token },
      signal: controller.signal,
    }).then(response => {
      if (active) setRequest({ key: requestKey, loading: false, data: readTrendData(response), error: "" });
    }).catch(() => {
      if (active) setRequest({ key: requestKey, loading: false, data: null, error: "Unable to load quote trends. Please try again." });
    });
    return () => { active = false; controller.abort(); };
  }, [period, token, requestKey, showUserFilter, userId, companyId, status]);

  const current = request.key === requestKey;
  const loading = !current || request.loading;
  const error = current ? request.error : "";
  const data = current ? request.data : null;
  const series = data?.series || [];
  const average = series.length ? data.totals[metric] / series.length : 0;
  const peak = series.length ? series.reduce((best, item) => item[metric] > best[metric] ? item : best) : null;
  const metricLabel = metric === "quotes" ? "Quote count" : "Quote value";
  const displayMetric = value => metric === "quotes" ? number(value, 1) : money(value);

  return (
    <Card className={"dashboard-panel quotes-trend-panel quotes-trend-panel--" + metric}>
      <Card.Header className="quotes-trend-panel__header">
        <div className="quotes-trend-panel__heading">
          <span className="quotes-trend-panel__icon" aria-hidden="true"><FontAwesomeIcon icon={faChartLine} /></span>
          <div>
            <h5 className="quotes-trend-panel__title">Quotes Trend</h5>
            <p className="quotes-trend-panel__subtitle">
              {series.length ? fullDate(series[0].date) + " \u2013 " + fullDate(series[series.length - 1].date) : "Explore your quote activity over time"}
            </p>
          </div>
        </div>
        <div className="quotes-trend-panel__controls">
          <div className="quotes-trend-segments quotes-trend-segments--period" role="group" aria-label="Date range">
            {PERIODS.map(item => (
              <button type="button" key={item.key} aria-label={item.name} aria-pressed={period === item.key}
                onClick={() => { setPeriod(item.key); if (MONTHLY_PERIODS.includes(item.key)) setView("bar"); }}>{item.label}</button>
            ))}
          </div>
          <div className="quotes-trend-panel__filters">
            {showUserFilter && (
              <Form.Select value={userId} onChange={(event) => setUserId(event.target.value)} aria-label="Filter by user">
                <option value="">All users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </Form.Select>
            )}
            <Form.Select value={companyId} onChange={(event) => setCompanyId(event.target.value)} aria-label="Filter by company">
              <option value="">All companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </Form.Select>
            <Form.Select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
              <option value="">All statuses</option>
              {STATUSES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Form.Select>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="quotes-trend-toolbar">
          <div className="quotes-trend-segments" role="group" aria-label="Chart metric">
            <button type="button" aria-pressed={metric === "quotes"} onClick={() => setMetric("quotes")}>Quote count</button>
            <button type="button" aria-pressed={metric === "amount"} onClick={() => setMetric("amount")}>Quote value</button>
          </div>
          <div className="quotes-trend-segments quotes-trend-segments--view" role="group" aria-label="Chart style">
            <button type="button" aria-label="Area chart" title="Area chart" aria-pressed={view === "area"} onClick={() => setView("area")}><FontAwesomeIcon icon={faChartArea} /></button>
            <button type="button" aria-label="Bar chart" title="Bar chart" aria-pressed={view === "bar"} onClick={() => setView("bar")}><FontAwesomeIcon icon={faChartBar} /></button>
          </div>
        </div>
        {loading ? (
          <div className="quotes-trend-state" role="status"><Spinner animation="border" size="sm" /><span>Loading quote trends...</span></div>
        ) : error ? (
          <div className="quotes-trend-state" role="alert">
            <FontAwesomeIcon icon={faChartLine} className="quotes-trend-state__icon" />
            <p>{error}</p><Button variant="outline-primary" size="sm" onClick={() => setRetry(value => value + 1)}>Retry</Button>
          </div>
        ) : !series.length ? (
          <div className="quotes-trend-state">
            <FontAwesomeIcon icon={faChartLine} className="quotes-trend-state__icon" />
            <strong>No quote data for the selected period.</strong><span>Try a longer date range to see earlier quotes.</span>
          </div>
        ) : (
          <>
            <div className="quotes-trend-stats">
              <div><span>Total quotes</span><strong>{number(data.totals.quotes)}</strong><small>In this period</small></div>
              <div><span>Total quote value</span><strong>{money(data.totals.amount)}</strong><small>In this period</small></div>
              <div><span>Daily average</span><strong>{displayMetric(average)}</strong><small>{metric === "quotes" ? "Quotes per day" : "Quote value per day"}</small></div>
              <div><span>Peak day</span><strong>{peak && peak[metric] !== 0 ? peak.label : "\u2014"}</strong><small>{peak ? displayMetric(peak[metric]) + (metric === "quotes" ? " quotes" : " in quote value") : ""}</small></div>
            </div>
            <div className="quotes-trend-legend">
              <span><i className="quotes-trend-legend__series" />{metricLabel}</span>
              <span><i className="quotes-trend-legend__average" />Daily average</span>
            </div>
            <TrendPlot key={period + metric + view} series={series} metric={metric} view={view} average={average} monthly={MONTHLY_PERIODS.includes(period)} />
            <div className="quotes-trend-panel__footnote">
              <span>Based on quote offer dates</span><span>Hover, tap or use arrow keys to explore</span>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
